import React, { FC, useMemo, useState } from 'react'
import { Divider, Flex, Tooltip, Typography } from 'antd'
import { Button, EmptyState, Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { getClassNames } from '../../../_utils/classnames'
import { InfoRow } from './brand-voice-decomposition'
import { tierCounts, type BrandVoiceDisplayTier } from './brand-voice-tier-visual'
import { CONTENT_DIRECTIVE_FIELDS } from '../content-directive-fields'
import { collapsedGuaranteedSpec, guaranteedDisplayRows } from './text-highlight'

/**
 * The settings-page view of a saved Brand Voice.
 *
 * Split out of {@link BrandVoiceWell} so it can be rendered and tested without the IoC-bound
 * services the well needs to fetch and edit.
 *
 * TEXT LEFT, RULES RIGHT: the same arrangement as the editor drawer, so this reads as a smaller
 * view of the same thing rather than a second, differently-shaped one.
 *
 * One tier shown at a time, chosen by `activeTier` — a controlled prop, not local state, because
 * the tags that switch it live in the Directive well's own header (next to Edit), above this
 * component entirely. `BrandVoiceWell` owns the selection so the header tag styling and this
 * pane's content can never disagree about which tier is "on".
 */
const TIER_LABEL: Record<BrandVoiceDisplayTier, string> = {
    guaranteed: 'Guaranteed',
    guidance: 'Guidance',
}

/** Items shown before the Guidance list collapses behind "+N more". */
const VISIBLE_ROW_CAP = 4

/**
 * Held constant across BOTH columns, every tier's pane, and whether or not "+N more" has been
 * clicked — switching tiers, or expanding a long list, must never resize the card. A floor alone
 * (`min-height`) does not do this: it stops the card from shrinking below some size, but nothing
 * stops it from growing past that size and then shrinking back on the next switch. A true fixed
 * height does — content that would overflow it scrolls internally instead of pushing the card
 * taller, always, including once expanded. It has to stay fixed post-expand specifically because
 * the Directive well around these columns is ITSELF a locked height with `overflow: hidden` (see
 * `BrandVoiceWell`) — a column that dropped its own scroll on expand had nowhere for the newly
 * revealed rows to go; the outer well simply clipped them instead of making them reachable.
 */
// Nudged up from 220: content that landed just a few pixels past the old value triggered a
// scrollbar for barely-there overflow, which read as a layout bug more than a real cap.
const PANE_COLLAPSED_HEIGHT = 248

interface IBrandVoiceSummary {
    body: string
    /** Null for a directive saved before `compiled_rules` was reliably persisted, or mid-first
     * compile — there is nothing to summarize, so the body renders on its own. */
    compiled: CompiledDirective | null
    activeTier: BrandVoiceDisplayTier
}

export const BrandVoiceSummary: FC<IBrandVoiceSummary> = ({ body, compiled, activeTier }) => {
    // Guidance spans all three prose tiers — see `DISPLAY_TIER_FOR`. Ordered mandates ->
    // persona -> conditional, matching the drawer and sailfish's `toDirectiveTierView`.
    const guidanceItems = useMemo(
        () => [...(compiled?.assertions ?? []), ...(compiled?.style ?? []), ...(compiled?.unmapped ?? [])],
        [compiled],
    )
    const guaranteedRows = useMemo(() => (compiled ? guaranteedDisplayRows(compiled) : []), [compiled])
    const counts = useMemo(() => tierCounts(compiled), [compiled])

    // PQueryBuilder's own row caps a list value at 3 entries and renders the rest as a bare
    // "+N" span — no tooltip, no click, no way to actually read the rest (confirmed off its
    // compiled source: `rule-row.js`'s `listTokens.slice(0, 3)`). We already have every value
    // before handing it to PQueryBuilder, so rather than forking a third-party row renderer to
    // add a hover, this builds the same "what's behind +N" answer as a wrapping Tooltip instead.
    const guaranteedOverflowTooltip = useMemo(() => {
        const truncatedRows = guaranteedRows.filter((row) => Array.isArray(row.value) && row.value.length > 3)
        if (truncatedRows.length === 0) return null

        return (
            <Flex vertical gap={4}>
                {truncatedRows.map((row) => {
                    const label = CONTENT_DIRECTIVE_FIELDS.find((f) => f.key === row.attribute)?.label ?? row.attribute
                    return (
                        <Typography.Text key={row.key} style={{ color: 'inherit' }}>
                            <strong>{label}:</strong> {(row.value as unknown[]).join(', ')}
                        </Typography.Text>
                    )
                })}
            </Flex>
        )
    }, [guaranteedRows])

    const [guidanceExpanded, setGuidanceExpanded] = useState(false)
    const visibleGuidance = guidanceExpanded ? guidanceItems : guidanceItems.slice(0, VISIBLE_ROW_CAP)
    const hiddenGuidanceCount = guidanceItems.length - visibleGuidance.length

    // Independent per column: expanding one is not a reason to also resize the other.
    // `paddingRight` keeps the scrollbar from sitting flush against the text — without it, the
    // scrollbar track landed directly on the last character of each line, right where the divider
    // to the "What we compiled" column already crowds the same edge.
    const collapsedPaneStyle = { height: PANE_COLLAPSED_HEIGHT, overflowY: 'auto' as const, paddingRight: 12 }

    if (!compiled) {
        return (
            <Well
                variant="ghost"
                padding="small"
                titleSize="sm"
                title={<Typography.Text strong>What you wrote</Typography.Text>}
            >
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{body}</Typography.Paragraph>
            </Well>
        )
    }

    return (
        // TEXT LEFT, RULES RIGHT — the same arrangement as the editor drawer. An earlier pass had
        // them flipped, which meant the two views of one directive put the same two things on
        // opposite sides: moving between them made the reader re-find both.
        <Flex gap={16} align="flex-start" className={getClassNames('bvw-columns')}>
            <Flex className={getClassNames('bvw-column')}>
                <Well
                    variant="ghost"
                    padding="small"
                    titleSize="sm"
                    style={{ width: '100%' }}
                    title={<Typography.Text strong>What you wrote</Typography.Text>}
                >
                    {/* Always the full body, never `Typography.Paragraph`'s `ellipsis` — that
                        feature measures its row-clamp via the container's live width through a
                        ResizeObserver, and when that measurement races (routine inside a flex
                        column whose width isn't settled on first paint), it silently falls back
                        to rendering the FULL, untruncated text — which is exactly what kept
                        escaping this box no matter what height/overflow was set on an ancestor.
                        A plain div with a real fixed height + `overflow-y: auto` can't lose that
                        race: the text is always fully rendered, and the box always clips/scrolls
                        it at a real height regardless of what antd's own measurement decided. */}
                    <div style={collapsedPaneStyle}>
                        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                            {body}
                        </Typography.Paragraph>
                    </div>
                </Well>
            </Flex>
            <Divider orientation="vertical" className="inner-well-divider vertical" />
            <Flex className={getClassNames('bvw-column')}>
                <Well
                    variant="ghost"
                    padding="small"
                    titleSize="sm"
                    style={{ width: '100%' }}
                    title={
                        <Typography.Text strong>
                            What we compiled — {TIER_LABEL[activeTier]}, {counts[activeTier]}
                        </Typography.Text>
                    }
                >
                    {/* Same reasoning as the left column: the fixed height + scroll lives on this
                        div rather than the Well's `bodyStyle`. Applied regardless of expanded
                        state — the outer Directive well is itself a LOCKED height with
                        `overflow: hidden` (see `BrandVoiceWell`), so a column that dropped its own
                        height/scroll on expand had nowhere for the extra content to go: it was
                        just clipped by the outer well instead of staying reachable. "+N more"
                        means "put more rows in the list", not "remove the box's own scrollbar" —
                        the box still scrolls to reach them. */}
                    <div style={collapsedPaneStyle}>
                        {/* The REAL PQueryBuilder, disabled — not a hand-rolled row. It owns the
                            field labels, the operator phrasing and the value chips, and reusing it
                            is what keeps this card, the editor drawer and the segment builder
                            saying the same sentence for the same condition.
                            Guidance cannot go through it, and that is not a gap: a builder renders
                            a PQuery condition, and Guidance has none. Being unexpressible as a
                            condition is exactly what makes it not-guaranteed. */}
                        {activeTier === 'guaranteed' ? (
                            guaranteedRows.length > 0 ? (
                                <Tooltip
                                    title={guaranteedOverflowTooltip}
                                    placement="bottom"
                                    // No tooltip at all when nothing is actually hidden — an empty
                                    // Tooltip wrapper still intercepts hover/focus for its own
                                    // (blank) popup, which is worse than no wrapper.
                                    open={guaranteedOverflowTooltip ? undefined : false}
                                >
                                    <div>
                                        <PQueryBuilder
                                            value={collapsedGuaranteedSpec(compiled)}
                                            fields={CONTENT_DIRECTIVE_FIELDS}
                                            disabled
                                            className={getClassNames('bvd-guaranteed-pqb')}
                                            onRuleCommit={() => {}}
                                            // `disabled` above stops the interactions; these blank
                                            // the "Add Rule"/"Add Group" labels — but the buttons
                                            // themselves are plain antd Buttons with no class of
                                            // their own, so blanking the label alone leaves an
                                            // empty, disabled icon button behind rather than hiding
                                            // it. See `.sw-v2-bvd-guaranteed-pqb` in
                                            // brand-voice-decomposition.scss for what actually
                                            // removes them.
                                            labels={{
                                                connectors: { and: '', or: '' },
                                                addCondition: '',
                                                addGroup: '',
                                            }}
                                        />
                                    </div>
                                </Tooltip>
                            ) : (
                                <EmptyState size="small" align="start" title="Nothing guaranteed yet" />
                            )
                        ) : guidanceItems.length > 0 ? (
                            <Flex vertical gap={8}>
                                <Flex vertical gap={8}>
                                    {visibleGuidance.map((item, i) => (
                                        <InfoRow key={i}>
                                            <Typography.Text>{item.text}</Typography.Text>
                                        </InfoRow>
                                    ))}
                                </Flex>
                                {hiddenGuidanceCount > 0 && (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ alignSelf: 'flex-start', padding: 0, height: 'auto' }}
                                        onClick={() => setGuidanceExpanded(true)}
                                    >
                                        +{hiddenGuidanceCount} more
                                    </Button>
                                )}
                            </Flex>
                        ) : (
                            <EmptyState size="small" align="start" title="No guidance yet" />
                        )}
                    </div>
                </Well>
            </Flex>
        </Flex>
    )
}
